import { useTranslation } from 'react-i18next';
import type { UpdateWhatsApp } from '@/types/orderUpdates';
import { formatUpdateClock, pickLang } from './updatesFormat';
import { deliveryTick } from './whatsappThread';

interface WhatsAppBubbleProps {
  message: UpdateWhatsApp;
}

export function WhatsAppBubble({ message }: WhatsAppBubbleProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const outgoing = message.from === 'boxy';
  const fallback = pickLang(message.fallback_label, lang);
  const text = message.text || fallback;
  const status = outgoing ? message.delivery_status : null;
  const tick = deliveryTick(status, t);
  const options = Array.isArray(message.options) ? message.options : [];
  const sender = outgoing
    ? String(t('updates_whatsapp_support'))
    : message.sender_label || String(t('updates_customer'));

  const content =
    message.type === 'button_reply' ? (
      <span className='inline-block rounded-full bg-[#25D366] px-3 py-1 text-[13px] font-medium text-white'>
        {message.choice || text}
      </span>
    ) : message.type === 'voice_note' ? (
      <span>
        <span aria-hidden>🎤 </span>
        {text || String(t('updates_voice_message'))}
      </span>
    ) : (
      <span className='whitespace-pre-line'>
        {text || (message.type === 'template' ? String(t('updates_order_update_sent')) : '')}
      </span>
    );

  return (
    <div
      data-testid='wa-bubble'
      data-from={outgoing ? 'boxy' : 'customer'}
      className={`flex max-w-[85%] flex-col gap-1 ${outgoing ? 'items-end self-end' : 'items-start self-start'}`}
    >
      <span className='text-[11px] text-sd-muted-foreground'>
        <span className={outgoing ? 'font-semibold uppercase' : 'font-semibold'}>{sender}</span>
        {' · '}
        {formatUpdateClock(message.at, lang)}
      </span>
      <div
        dir='auto'
        className={`rounded-2xl border border-solid px-3 py-2 text-[13px] ${
          outgoing
            ? 'rounded-se-none border-[#bedbb0] bg-[#DCF8C6] text-[#303030]'
            : 'rounded-ss-none border-sd-border bg-sd-card'
        }`}
      >
        {content}
        {message.type === 'menu' && options.length ? (
          <div className='mt-2 flex flex-wrap gap-1.5'>
            {options.map((option, i) => (
              <span
                key={`${option}-${i}`}
                className='rounded-full border border-solid border-[#25D366] px-2.5 py-0.5 text-[12px] text-[#128C7E]'
              >
                {option}
              </span>
            ))}
          </div>
        ) : null}
        {tick ? (
          <span
            role='img'
            aria-label={tick.label}
            className={`ms-2 align-bottom text-[11px] ${tick.className}`}
          >
            {tick.mark}
          </span>
        ) : null}
      </div>
      {status === 'failed' ? (
        <span className='text-[11px] font-medium text-[hsl(0,70%,45%)]'>
          {String(t('updates_not_delivered'))}
        </span>
      ) : null}
    </div>
  );
}
